import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import './LoginPage.css';

const isNative = Capacitor.isNativePlatform();

// ── Storage helpers — native uses Preferences, web uses localStorage ──
const storage = {
  set: async (key, value) => {
    if (isNative) {
      await Preferences.set({ key, value: String(value) });
    } else {
      localStorage.setItem(key, value);
    }
  },
  get: async (key) => {
    if (isNative) {
      const { value } = await Preferences.get({ key });
      return value;
    } else {
      return localStorage.getItem(key);
    }
  },
  remove: async (key) => {
    if (isNative) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  },
};

function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const currentYear = new Date().getFullYear();

  // ── Auto-login if valid token exists ──
  useEffect(() => {
    const checkExistingToken = async () => {
      try {
        let token, userId;
        if (Capacitor.isNativePlatform()) {
          const t = await Preferences.get({ key: 'token' });
          const u = await Preferences.get({ key: 'userId' });
          token = t.value;
          userId = u.value;
          // Also sync to localStorage so rest of app works
          if (token) {
            localStorage.setItem('token', token);
            localStorage.setItem('userId', userId);
          }
        } else {
          token = localStorage.getItem('token');
          userId = localStorage.getItem('userId');
        }
        if (token && userId) {
          // Verify token is still valid
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/users/${userId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.status === 200) {
            const redirectTo = searchParams.get('redirect');
            navigate(redirectTo || '/dashboard');
            return;
          }
        }
      } catch (e) {
        // Token invalid or expired — clear and show login
        await storage.remove('token');
        await storage.remove('userId');
      } finally {
        setCheckingAuth(false);
      }
    };
    checkExistingToken();
  }, []);

  useEffect(() => {
    if (searchParams.get('registered') === 'true') {
      setSuccessMessage('Registration successful! Please sign in to get started.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const lowercaseUsername = username.toLowerCase();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/users/login`,
        { username: lowercaseUsername, password }
      );
      const { token, userId } = response.data;

      // Save to localStorage (web) AND Preferences (mobile native)
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);
      if (Capacitor.isNativePlatform()) {
        await Preferences.set({ key: 'token', value: token });
        await Preferences.set({ key: 'userId', value: String(userId) });
      }

      // Redirect to invite link if present, otherwise dashboard
      const redirectTo = searchParams.get('redirect');
      const pendingInvite = localStorage.getItem('pendingCircleInvite');

      if (redirectTo) {
        navigate(redirectTo);
      } else if (pendingInvite) {
        localStorage.removeItem('pendingCircleInvite');
        navigate(`/circles/join?token=${pendingInvite}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      if (error.response) {
        setError(error.response.data.message);
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show blank screen while checking for existing session
  if (checkingAuth) {
    return <div className="login-page" />;
  }

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="wave-pattern"></div>
      </div>

      <div className="login-container">
        {/* Logo Section */}
        <div className="login-logo">
          <div className="wave-symbol">
            <div className="wave-element"></div>
            <div className="wave-element-small"></div>
          </div>
          <div className="logo-text">
            <span className="logo-air">Air</span>Flyp
          </div>
        </div>

        {/* Welcome Message */}
        <div className="welcome-section">
          <h1>Welcome Back</h1>
          <p>Sign in to your thoughtfully designed workspace</p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="success-message" style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
            color: '#059669',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            fontSize: '0.95rem',
            fontWeight: '600',
            textAlign: 'center',
          }}>
            <p style={{ margin: 0 }}>{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <div className="error-icon">⚠️</div>
            <p>{error}</p>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <div className="input-wrapper">
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
                disabled={isLoading}
              />
              <div className="input-icon"></div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={`login-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="loading-content">
                <div className="loading-spinner"></div>
                <span>Signing you in...</span>
              </div>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Action Links */}
        <div className="action-links">
          <Link to="/request-reset" className="reset-link">
            Forgot your password?
          </Link>

          <div className="divider">
            <span>New to AirFlyp?</span>
          </div>

          <Link
            to={searchParams.get('redirect') ? `/register?redirect=${encodeURIComponent(searchParams.get('redirect'))}` : '/register'}
            className="register-link"
          >
            Create your account
          </Link>
        </div>

        {/* Footer */}
        <footer className="login-footer">
          <p>&copy; {currentYear} AirFlyp — built for modern teams.</p>
        </footer>
      </div>
    </div>
  );
}

export default LoginPage;